import { Avatar, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useLocation } from "react-router-dom";

const Tag = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})<any>(({ theme, status }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  textTransform: "capitalize",
  ...(status === "display" && {
    color: "#00AAB0",
    backgroundColor: "#eff1f7",
    borderColor: "#00AAB0",
    border: "1px solid",
  }),
}));

function RightSide({ data }: any) {
  const sideItems = data?.practices || data?.policies;
  const location = useLocation();
  const { translator } = useLanguageTranslator();
  const isPolicies = location?.pathname?.includes("policies") || false;

  return (
    <>
      <Typography
        variant="h4"
        fontWeight={300}
        color={"#494E54"}
        fontSize={"12px"}
        gutterBottom
      >
        {translator("Submitted by")}:
      </Typography>
      <Stack direction={{ lg: "row" }} alignItems={{ lg: "center" }} gap={2}>
        <Avatar
          alt={`${data?.submittedBy?.firstName} ${data?.submittedBy?.lastName}`}
          src="/static/images/avatar/1.jpg"
        />
        <Typography
          variant="h4"
          fontWeight={600}
          fontSize={"15px"}
          color={"#494E54"}
          gutterBottom
        >
          {data?.submittedBy?.firstName} {data?.submittedBy?.lastName}
        </Typography>
      </Stack>
      <Typography
        variant="h4"
        fontWeight={300}
        color={"#494E54"}
        fontSize={"12px"}
        mt={2}
        gutterBottom
      >
        {translator("generic.Date")} & {translator("generic.Time")}:
      </Typography>
      <Typography
        variant="h4"
        fontWeight={600}
        fontSize={"15px"}
        color={"#494E54"}
        gutterBottom
      >
        {data?.date}
      </Typography>
      <Typography
        variant="h4"
        fontWeight={300}
        color={"#494E54"}
        fontSize={"12px"}
        my={2}
        gutterBottom
      >
        {data?.policies?.length > 0 ? "Policies" : "Practices"}
      </Typography>
      <ConditionalRenderer
        condition={data?.practices?.length <= 0 || data?.policies?.length <= 0}
      >
        {translator("None")}
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={data?.practices?.length > 0 || data?.policies?.length > 0}
      >
        <Stack
          direction={{ lg: "column" }}
          alignItems={{ lg: "start" }}
          gap={2}
          height={"7dvh"}
          sx={{ overflowY: "auto" }}
        >
          {sideItems?.map((elem: any) => (
            <Tag
              label={elem?.title?.slice(0, 30)}
              title={elem?.title}
              status="display"
            />
          ))}
        </Stack>
      </ConditionalRenderer>
      <Typography
        variant="h4"
        fontWeight={300}
        color={"#494E54"}
        fontSize={"12px"}
        my={2}
        gutterBottom
      >
        {translator("People")}
      </Typography>
      <ConditionalRenderer condition={data?.people?.length <= 0}>
        {translator("None")}
      </ConditionalRenderer>
      <ConditionalRenderer condition={data?.people?.length > 0}>
        <Stack
          direction={{ lg: "column" }}
          alignItems={{ lg: "start" }}
          gap={2}
          height={"7dvh"}
          sx={{ overflowY: "auto" }}
        >
          {data?.people?.map((elem: any) => (
            <Tag
              label={`${elem?.firstname} ${elem?.lastname}`}
              title={`${elem?.firstname} ${elem?.lastname}`}
              status="display"
            />
          ))}
        </Stack>
      </ConditionalRenderer>
      <Stack
        direction={{ lg: "row" }}
        alignItems={{ lg: "center" }}
        gap={2}
        my={3}
      >
        <Stack direction={{ lg: "column" }}>
          <Typography
            variant="h4"
            fontWeight={300}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {translator("Country")}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {data?.country?.name || ""}
          </Typography>
        </Stack>
        <Stack direction={{ lg: "column" }}>
          <Typography
            variant="h4"
            fontWeight={300}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {translator("generic.Date Voted")}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            27 May 2024
          </Typography>
        </Stack>
        <Stack direction={{ lg: "column" }}>
          <Typography
            variant="h4"
            fontWeight={300}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {translator("generic.Geographical Zone")}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {data?.geographicalArea?.name || data?.practiceType?.name || ""}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        direction={{ lg: "row" }}
        alignItems={{ lg: "center" }}
        gap={2}
        my={3}
      >
        <Stack direction={{ lg: "column" }}>
          <Typography
            variant="h4"
            fontWeight={300}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {translator("Theme")}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {data?.theme?.title || ""}
          </Typography>
        </Stack>
        <Stack direction={{ lg: "column" }}>
          <Typography
            variant="h4"
            fontWeight={300}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {translator("dashboardTableHead.Category")}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={"#494E54"}
            fontSize={"12px"}
            gutterBottom
          >
            {data?.policyCategory?.name || data?.practiceCategory?.name || ""}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction={{ lg: "column" }} minWidth={300} maxWidth={300}>
        <Typography
          variant="h4"
          fontWeight={300}
          color={"#494E54"}
          fontSize={"12px"}
          gutterBottom
        >
          External {isPolicies ? "Practice" : "Policy"} Link
        </Typography>
        <Typography
          variant="h4"
          fontWeight={700}
          color={"#00AAB0"}
          fontSize={"14px"}
          gutterBottom
          sx={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          <a
            href={data?.link || "#"}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#00AAB0",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "14px",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "normal",
            }}
          >
            {data?.link || ""}
          </a>
        </Typography>
      </Stack>
    </>
  );
}

export { RightSide };
